import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MenuItem, TextField, CircularProgress, InputAdornment } from '@mui/material';
import { getMasterOptions } from '../../services/platform/masterService';
import { COUNTRY_DEFAULTS, registerCountryOptions } from '../../constants/countryDefaults';

export type GeoMasterCountryOption = {
  name: string;
  _id?: string;
  currency?: string;
  timezone?: string;
  timezones?: string[];
  currencies?: string[];
};

interface Props {
  label: string;
  masterType: string;
  value: string;
  onChange: (code: string, option?: GeoMasterCountryOption) => void;
  filter?: { country?: string; state?: string };
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
}

const COUNTRY_FALLBACK = Object.entries(COUNTRY_DEFAULTS).map(([code, d]) => ({
  _id: code,
  code,
  name: d.label,
  currency: d.currency,
  timezone: d.timezone,
  timezones: d.timezones,
  currencies: d.currencies,
}));

function isCountryMaster(type: string) {
  return type === 'countries' || type === 'transport_countries';
}

/** Dropdown selector for geo masters — countries load the full ISO master list. */
export default function GeoMasterSelect({
  label, masterType, value, onChange, filter, required, disabled, helperText, error,
}: Props) {
  const { data: options = [], isLoading } = useQuery({
    queryKey: ['master-options', masterType, filter?.country, filter?.state, 'countries-fallback'],
    queryFn: async () => {
      try {
        const rows = await getMasterOptions(masterType, filter);
        if (Array.isArray(rows) && rows.length) return rows;
      } catch {
        /* fall through to country fallback */
      }
      if (isCountryMaster(masterType)) return COUNTRY_FALLBACK;
      return [];
    },
    enabled: !!masterType,
  });

  useEffect(() => {
    if (!isCountryMaster(masterType) || !options.length) return;
    registerCountryOptions(options.map((o) => ({
      code: o.code,
      name: o.name,
      currency: o.currency,
      timezone: o.timezone,
      timezones: o.timezones,
      currencies: o.currencies,
    })));
  }, [masterType, options]);

  if (isLoading) {
    return (
      <TextField
        select fullWidth size="small" label={label} value="" disabled
        InputProps={{
          endAdornment: (
            <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
          ),
        }}
      >
        <MenuItem value="">Loading…</MenuItem>
      </TextField>
    );
  }

  if (!options.length) {
    return (
      <TextField
        select fullWidth size="small" label={label} value="" required={required} disabled
        error={error}
        helperText={helperText || `No ${label.toLowerCase()} records — add parent masters first`}
      >
        <MenuItem value="">— No options —</MenuItem>
      </TextField>
    );
  }

  return (
    <TextField
      select fullWidth size="small" label={label} value={value} required={required} disabled={disabled}
      error={error}
      helperText={helperText}
      onChange={(e) => {
        const opt = options.find((o) => o.code === e.target.value);
        onChange(
          e.target.value,
          opt
            ? {
              name: opt.name,
              _id: opt._id,
              currency: opt.currency,
              timezone: opt.timezone,
              timezones: opt.timezones,
              currencies: opt.currencies,
            }
            : undefined,
        );
      }}
    >
      <MenuItem value="">— Select —</MenuItem>
      {options.map((o) => (
        <MenuItem key={o._id || o.code} value={o.code}>{o.name} ({o.code})</MenuItem>
      ))}
    </TextField>
  );
}
